import React, { useState, useEffect, useCallback } from 'react';
import { BookOpen, Heart, Star, Calendar, Plus, Edit, Trash2, Download, Bell, Target, BarChart3, TrendingUp, Award, Lock, Flame, Gift, User, Settings, Sparkles, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { useLocation } from 'react-router-dom';
import { 
  getManifestationCategories, 
  getUserSubscriptions, 
  subscribeToCategory, 
  unsubscribeFromCategory,
  getDailyManifestationForUser,
  markManifestationAsDelivered,
  markManifestationAsRead,
  checkDailyManifestationAvailable,
  hasReceivedTodaysManifestation,
  getTodaysUnreadManifestation,
  ManifestationCategory,
  getUserManifestations,
  updateUserManifestationStatus,
  UserManifestation
} from '../lib/supabase';
import { AnalyticsInsights } from './AnalyticsInsights';

export const Profile: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<'books' | 'manifestations' | 'cards' | 'gratitude' | 'success-tracking' | 'analytics-insights'>('manifestations');
  const [gratitudeItems, setGratitudeItems] = useState([
    'My health and vitality',
    'The love and support of my family',
    'New opportunities coming my way',
  ]);
  const [newGratitude, setNewGratitude] = useState<string>('');
  
  // Daily manifestation states
  const [categories, setCategories] = useState<ManifestationCategory[]>([]);
  const [userSubscriptions, setUserSubscriptions] = useState<any[]>([]);
  const [dailyManifestation, setDailyManifestation] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [hasUnreadManifestation, setHasUnreadManifestation] = useState(false);
  const [hasReceivedToday, setHasReceivedToday] = useState(false);
  const [manifestationDelivered, setManifestationDelivered] = useState(false);

  // User manifestations
  const [userManifestations, setUserManifestations] = useState<UserManifestation[]>([]);
  const [manifestationsLoading, setManifestationsLoading] = useState(false);

  // User profile data
  const userProfile = (user as any)?.profile;
  const consecutiveLoginDays = userProfile?.consecutive_login_days || 0;
  const commitmentTier = userProfile?.commitment_tier || 'seeker';

  // Check for tab in location state (from router navigation)
  useEffect(() => {
    if (location.state && location.state.activeTab) {
      const tabName = location.state.activeTab;
      switch (tabName) {
        case 'books':
        case 'manifestations':
        case 'cards':
        case 'gratitude':
        case 'success-tracking':
        case 'analytics-insights':
          setActiveTab(tabName);
          break;
        default:
          // If invalid tab name, default to manifestations
          setActiveTab('manifestations');
      }
      
      // Clear the state after using it to prevent persisting across navigations
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  useEffect(() => {
    if (user) {
      loadCategories();
      loadUserSubscriptions();
      checkManifestationStatus();
      loadUserManifestations();
    }
  }, [user]);

  const loadCategories = async () => {
    try {
      const data = await getManifestationCategories();
      setCategories(data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadUserSubscriptions = async () => {
    if (!user) return;
    try {
      const data = await getUserSubscriptions(user.id);
      setUserSubscriptions(data);
    } catch (error) {
      console.error('Error loading subscriptions:', error);
    }
  };

  const loadUserManifestations = async () => {
    if (!user) return;
    
    setManifestationsLoading(true);
    try {
      const manifestations = await getUserManifestations(user.id);
      setUserManifestations(manifestations);
    } catch (error) {
      console.error('Error loading user manifestations:', error);
    } finally {
      setManifestationsLoading(false);
    }
  };

  const checkManifestationStatus = async () => {
    if (!user) return;
    
    try {
      // Check if user has already received today's manifestation
      const receivedToday = await hasReceivedTodaysManifestation(user.id);
      setHasReceivedToday(receivedToday);
      
      if (receivedToday) {
        // User has already received and read today's manifestation
        setHasUnreadManifestation(false);
        setDailyManifestation(null);
        setManifestationDelivered(true);
        return;
      }
      
      // Check if there's an unread manifestation from today
      const unreadManifestation = await getTodaysUnreadManifestation(user.id);
      if (unreadManifestation) {
        // User has a manifestation delivered but not read yet
        setDailyManifestation(unreadManifestation);
        setHasUnreadManifestation(false); // Don't show notification, show the manifestation directly
        setManifestationDelivered(true); // Button should be disabled since manifestation is already delivered
        return;
      }
      
      // Check if a new manifestation is available
      const manifestationAvailable = await checkDailyManifestationAvailable(user.id);
      setHasUnreadManifestation(manifestationAvailable);
      setManifestationDelivered(false); // Button should be enabled
      
    } catch (error) {
      console.error('Error checking manifestation status:', error);
    }
  };

  const handleCategoryToggle = async (categoryId: string, currentlyChecked: boolean) => {
    if (!user) return;
    
    try {
      if (currentlyChecked) {
        await unsubscribeFromCategory(user.id, categoryId);
      } else {
        await subscribeToCategory(user.id, categoryId);
      }
      await loadUserSubscriptions();
      // Recheck manifestation status after subscription change
      await checkManifestationStatus();
    } catch (error) {
      console.error('Error toggling subscription:', error);
    }
  };

  const getDailyManifestation = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const manifestation = await getDailyManifestationForUser(user.id);
      if (manifestation) {
        setDailyManifestation(manifestation);
        await markManifestationAsDelivered(user.id, manifestation.manifestation_id);
        setHasUnreadManifestation(false);
        setManifestationDelivered(true); // Disable button after delivery
      } else {
        alert('No new manifestation available today. Make sure you\'re subscribed to at least one category!');
      }
    } catch (error) {
      console.error('Error getting daily manifestation:', error);
      alert('Error getting your daily manifestation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const receiveIt = async () => {
    if (!user || !dailyManifestation) return;
    
    try {
      await markManifestationAsRead(user.id, dailyManifestation.manifestation_id);
      setDailyManifestation(null);
      setHasReceivedToday(true);
      setManifestationDelivered(true);
    } catch (error) {
      console.error('Error marking as received:', error);
    }
  };

  const addGratitude = () => {
    if (newGratitude.trim()) {
      setGratitudeItems([...gratitudeItems, newGratitude.trim()]);
      setNewGratitude('');
      
      // Mark gratitude as completed for today (for notification system)
      localStorage.setItem(`gratitude_completed_${new Date().toDateString()}`, 'true');
    }
  };

  const removeGratitude = (index: number) => {
    setGratitudeItems(gratitudeItems.filter((_, i) => i !== index));
  };

  const isSubscribed = (categoryId: string) => {
    return userSubscriptions.some(sub => sub.category_id === categoryId && sub.is_active);
  };

  // Check if button should be disabled
  const isButtonDisabled = () => {
    return loading || 
           userSubscriptions.length === 0 || 
           hasReceivedToday || 
           manifestationDelivered ||
           !hasUnreadManifestation;
  };

  // Get button text based on state
  const getButtonText = () => {
    if (loading) return t('common.loading');
    if (hasReceivedToday) return t('profile.receivedToday');
    if (manifestationDelivered && dailyManifestation) return t('profile.manifestationDelivered');
    if (userSubscriptions.length === 0) return t('profile.subscribeToCategoriesFirst');
    if (!hasUnreadManifestation) return t('profile.noNewManifestation');
    return t('profile.getManifestation');
  };

  // Calculate meditation unlock status
  const getMeditationUnlockStatus = () => {
    if (consecutiveLoginDays >= 90) return { weeks: 4, unlocked: true, nextMilestone: null };
    if (consecutiveLoginDays >= 60) return { weeks: 2, unlocked: true, nextMilestone: { days: 90, weeks: 4 } };
    if (consecutiveLoginDays >= 30) return { weeks: 1, unlocked: true, nextMilestone: { days: 60, weeks: 2 } };
    return { weeks: 0, unlocked: false, nextMilestone: { days: 30, weeks: 1 } };
  };

  const meditationStatus = getMeditationUnlockStatus();

  // Handle manifestation status change
  const handleManifestationStatusChange = async (manifestationId: string, newStatus: string) => {
    if (!user) return;
    
    try {
      await updateUserManifestationStatus(user.id, manifestationId, newStatus);
      // Refresh the list
      loadUserManifestations();
    } catch (error) {
      console.error('Error updating manifestation status:', error);
    }
  };

  const tabs = [
    { id: 'books', label: t('profile.myBooks'), icon: BookOpen },
    { id: 'manifestations', label: t('profile.manifestations'), icon: Star },
    { id: 'cards', label: t('profile.dailyCards'), icon: Calendar },
    { id: 'gratitude', label: t('profile.gratitude'), icon: Heart },
    { id: 'success-tracking', label: t('profile.successTracking'), icon: Target },
    { id: 'analytics-insights', label: t('profile.analyticsInsights'), icon: BarChart3 },
  ];

  if (!user) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 text-center">
        <p className="text-gray-600">{t('profile.loginToView')}</p>
      </div>
    );
  }

  return (
    <div className="w-full py-8 sm:py-12">
      {/* Profile Header */}
      <div className="bg-gradient-to-br from-white to-earth-sand/30 rounded-2xl border-2 border-earth-clay/30 p-6 sm:p-8 lg:p-10 mb-8 sm:mb-12 shadow-lg">
        <div className="flex flex-col sm:flex-row items-center sm:items-start space-y-6 sm:space-y-0 sm:space-x-8 text-center sm:text-left">
          <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-earth-bronze to-earth-terracotta rounded-2xl flex items-center justify-center flex-shrink-0 shadow-xl transform hover:rotate-6 transition-transform duration-300">
            <Heart size={32} className="sm:w-10 sm:h-10 text-white" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-earth-stone mb-2">
              {user.user_metadata?.full_name || user.email?.split('@')[0] || 'User'}
            </h1>
            <p className="text-sm sm:text-base text-gray-600 font-medium">{t('profile.memberSince')} {new Date(user.created_at).toLocaleDateString()}</p>
            <div className="flex flex-wrap items-center gap-4 sm:gap-6 mt-4 text-sm sm:text-base">
              <span className="bg-earth-moss/10 text-earth-forest px-4 py-2 rounded-xl font-semibold">{userManifestations.filter(m => m.status === 'manifested').length} {t('profile.manifestationsAchieved')}</span>
              <span className="bg-earth-bronze/10 text-earth-bronze px-4 py-2 rounded-xl font-semibold">{userSubscriptions.length} {t('profile.categoriesSubscribed')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Guided Meditation Unlock Section */}
      <div className={`bg-gradient-to-br from-white via-amber-50 to-orange-50 border-2 rounded-2xl p-6 sm:p-8 mb-8 sm:mb-12 shadow-lg ${
        commitmentTier === 'seeker' ? 'border-gray-300 opacity-75' : 'border-amber-200'
      }`}>
        <div className="flex flex-col lg:flex-row items-start lg:justify-between space-y-4 lg:space-y-0">
          <div className="flex-1">
            <div className="flex flex-col sm:flex-row items-center sm:items-start space-y-3 sm:space-y-0 sm:space-x-3 mb-4 text-center sm:text-left">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                commitmentTier === 'seeker' ? 'bg-gray-300' : 'bg-gradient-to-r from-amber-500 to-orange-500'
              }`}>
                {commitmentTier === 'seeker' ? (
                  <Lock size={20} className="text-gray-600" />
                ) : (
                  <Gift size={20} className="text-white" />
                )}
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">{t('profile.guidedMeditationUnlock')}</h3>
                <div className="flex items-center space-x-2">
                  <Flame size={14} className="sm:w-4 sm:h-4 text-orange-500" />
                  <span className="text-xs sm:text-sm font-medium text-gray-700">
                    {consecutiveLoginDays} {t('profile.dayStreak')}
                  </span>
                </div>
              </div>
            </div>

            {commitmentTier === 'seeker' ? (
              <div className="bg-gray-100 rounded-lg p-3 sm:p-4 mb-4">
                <p className="text-gray-600 text-xs sm:text-sm mb-2">
                  <Lock size={12} className="sm:w-3.5 sm:h-3.5 inline mr-1" />
                  {t('profile.featureRequiresPaid')}
                </p>
                <button className="bg-purple-600 hover:bg-purple-700 text-white px-3 sm:px-4 py-1 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors duration-200">
                  {t('profile.upgradeCommitment')}
                </button>
              </div>
            ) : (
              <div className="space-y-3 sm:space-y-4">
                {/* Progress Milestones */}
                <div className="grid grid-cols-3 gap-2 sm:gap-4">
                  <div className={`text-center p-3 rounded-lg border-2 ${
                    consecutiveLoginDays >= 30 ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'
                  }`}>
                    <div className="text-base sm:text-lg font-bold text-gray-900">30 {t('profile.days')}</div>
                    <div className="text-xs text-gray-600">1 {t('profile.weekMeditation')}</div>
                    {consecutiveLoginDays >= 30 && (
                      <Award size={14} className="sm:w-4 sm:h-4 text-green-600 mx-auto mt-1" />
                    )}
                  </div>
                  <div className={`text-center p-3 rounded-lg border-2 ${
                    consecutiveLoginDays >= 60 ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'
                  }`}>
                    <div className="text-base sm:text-lg font-bold text-gray-900">60 {t('profile.days')}</div>
                    <div className="text-xs text-gray-600">2 {t('profile.weeksMeditation')}</div>
                    {consecutiveLoginDays >= 60 && (
                      <Award size={14} className="sm:w-4 sm:h-4 text-green-600 mx-auto mt-1" />
                    )}
                  </div>
                  <div className={`text-center p-3 rounded-lg border-2 ${
                    consecutiveLoginDays >= 90 ? 'border-green-300 bg-green-50' : 'border-gray-200 bg-gray-50'
                  }`}>
                    <div className="text-base sm:text-lg font-bold text-gray-900">90 {t('profile.days')}</div>
                    <div className="text-xs text-gray-600">4 {t('profile.weeksMeditation')}</div>
                    {consecutiveLoginDays >= 90 && (
                      <Award size={14} className="sm:w-4 sm:h-4 text-green-600 mx-auto mt-1" />
                    )}
                  </div>
                </div>

                {/* Current Status */}
                {meditationStatus.unlocked ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Award size={14} className="sm:w-4 sm:h-4 text-green-600" />
                      <span className="text-sm sm:text-base font-medium text-green-900">
                        {t('profile.unlockedMeditation', { 
                          weeks: meditationStatus.weeks, 
                          plural: meditationStatus.weeks > 1 ? 's' : '' 
                        })}
                      </span>
                    </div>
                    {meditationStatus.nextMilestone && (
                      <p className="text-green-700 text-xs sm:text-sm">
                        {t('profile.keepGoingDays', { 
                          days: meditationStatus.nextMilestone.days - consecutiveLoginDays,
                          weeks: meditationStatus.nextMilestone.weeks
                        })}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 sm:p-4">
                    <p className="text-amber-800 text-xs sm:text-sm">
                      {t('profile.daysToUnlock', {
                        days: meditationStatus.nextMilestone!.days - consecutiveLoginDays,
                        weeks: meditationStatus.nextMilestone!.weeks
                      })}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Notification for daily manifestation */}
      {hasUnreadManifestation && !hasReceivedToday && !manifestationDelivered && (
        <div className="bg-gradient-to-r from-earth-sand to-earth-clay border border-earth-terracotta/30 rounded-lg p-4 mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-3 sm:space-y-0">
            <div className="flex items-start space-x-3">
              <Bell size={18} className="sm:w-5 sm:h-5 text-earth-bronze flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm sm:text-base font-medium text-earth-stone">{t('profile.dailyManifestationReady')}</h3>
                <p className="text-xs sm:text-sm text-gray-700 leading-relaxed">{t('profile.newManifestationWaiting')}</p>
              </div>
            </div>
            <button
              onClick={getDailyManifestation}
              disabled={isButtonDisabled()}
              className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors duration-200 w-full sm:w-auto ${
                isButtonDisabled()
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-earth-bronze hover:bg-earth-warmstone text-white'
              }`}
            >
              {getButtonText()}
            </button>
          </div>
        </div>
      )}

      {/* Daily Manifestation Display */}
      {dailyManifestation && !hasReceivedToday && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 sm:p-8 mb-6 sm:mb-8">
          <div className="text-center">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-earth-bronze to-earth-terracotta rounded-full flex items-center justify-center mx-auto mb-4">
              <Star size={16} className="sm:w-5 sm:h-5 text-white" />
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">{t('profile.todaysManifestation')}</h2>
            <div className="inline-block px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-medium mb-4" 
                 style={{ backgroundColor: dailyManifestation.category_color + '20', color: dailyManifestation.category_color }}>
              {dailyManifestation.category_name}
            </div>
            <p className="text-lg sm:text-xl text-gray-800 italic leading-relaxed mb-6 px-2">
              "{dailyManifestation.manifestation_text}"
            </p>
            <button
              onClick={receiveIt}
              className="bg-gradient-to-r from-earth-bronze to-earth-terracotta hover:from-earth-warmstone hover:to-earth-bronze text-white px-6 sm:px-8 py-2 sm:py-3 rounded-lg text-sm sm:text-base font-medium transition-all duration-200 transform hover:scale-105"
            >
              {t('profile.iReceiveIt')}
            </button>
          </div>
        </div>
      )}

      {/* Message when user has already received today's manifestation */}
      {hasReceivedToday && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4 sm:p-6 mb-6 sm:mb-8 text-center">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <Heart size={16} className="sm:w-5 sm:h-5 text-white" />
          </div>
          <h3 className="text-base sm:text-lg font-medium text-green-900 mb-2">{t('profile.receivedTodaysManifestationTitle')}</h3>
          <p className="text-sm sm:text-base text-green-700">{t('profile.nextManifestationTomorrow')}</p>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6 sm:mb-8">
        <div className="flex space-x-4 sm:space-x-6 lg:space-x-8 overflow-x-auto pb-2">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center space-x-1 sm:space-x-2 py-2 sm:py-3 font-medium transition-colors duration-200 border-b-2 whitespace-nowrap text-xs sm:text-sm ${
                  activeTab === tab.id
                    ? 'text-earth-bronze border-earth-bronze'
                    : 'text-gray-500 border-transparent hover:text-gray-700'
                }`}
              >
                <Icon size={16} className="sm:w-4 sm:h-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="space-y-4 sm:space-y-6">
        {/* Daily Cards Tab */}
        {activeTab === 'cards' && (
          <div>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 space-y-3 sm:space-y-0">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">{t('profile.dailyManifestationSubscriptions')}</h2>
              <button
                onClick={getDailyManifestation}
                disabled={isButtonDisabled()}
                className={`px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors duration-200 w-full sm:w-auto ${
                  isButtonDisabled()
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-purple-600 hover:bg-purple-700 text-white'
                }`}
              >
                {getButtonText()}
              </button>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6 sm:mb-8">
              {categories.map((category) => {
                const subscribed = isSubscribed(category.id);
                return (
                  <div key={category.id} className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h3 className="text-sm sm:text-base font-medium text-gray-900 mb-1">{category.name}</h3>
                        <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">{category.description}</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer ml-3 sm:ml-4 flex-shrink-0">
                        <input
                          type="checkbox"
                          checked={subscribed}
                          onChange={() => handleCategoryToggle(category.id, subscribed)}
                          className="sr-only peer"
                        />
                        <div className="w-10 h-5 sm:w-11 sm:h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-earth-bronze/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 sm:after:h-5 sm:after:w-5 after:transition-all peer-checked:bg-earth-bronze"></div>
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>

            {userSubscriptions.length === 0 && (
              <div className="text-center py-6 sm:py-8">
                <p className="text-sm sm:text-base text-gray-600 mb-4 px-4">{t('profile.subscribeToReceiveDaily')}</p>
              </div>
            )}

            <div className="bg-gray-50 rounded-lg p-4 sm:p-6 text-center">
              <h3 className="text-sm sm:text-base font-medium text-gray-900 mb-2">{t('profile.howDailyManifestationsWork')}</h3>
              <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
                {t('profile.dailyManifestationExplanation')}
              </p>
            </div>
          </div>
        )}

        {/* Success Tracking Tab */}
        {activeTab === 'success-tracking' && (
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">{t('profile.personalSuccessTracking')}</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
              {/* Login Streak */}
              <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
                <div className="flex items-center space-x-3 mb-3 sm:mb-4">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-orange-100 rounded-full flex items-center justify-center">
                    <Flame size={16} className="sm:w-5 sm:h-5 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="text-sm sm:text-base font-medium text-gray-900">{t('profile.loginStreak')}</h3>
                    <p className="text-xl sm:text-2xl font-bold text-earth-terracotta">{consecutiveLoginDays} {t('profile.days')}</p>
                  </div>
                </div>
                <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">{t('profile.loginStreakDescription')}</p>
              </div>

              {/* Manifestations Created */}
              <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
                <div className="flex items-center space-x-3 mb-3 sm:mb-4">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 rounded-full flex items-center justify-center">
                    <Star size={16} className="sm:w-5 sm:h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-sm sm:text-base font-medium text-gray-900">{t('profile.manifestations')}</h3>
                    <p className="text-xl sm:text-2xl font-bold text-earth-bronze">{userManifestations.length}</p>
                  </div>
                </div>
                <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">{t('profile.manifestationsDescription')}</p>
              </div>

              {/* Success Rate */}
              <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
                <div className="flex items-center space-x-3 mb-3 sm:mb-4">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <TrendingUp size={16} className="sm:w-5 sm:h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-sm sm:text-base font-medium text-gray-900">{t('profile.successRate')}</h3>
                    <p className="text-xl sm:text-2xl font-bold text-green-600">
                      {userManifestations.length > 0 
                        ? Math.round((userManifestations.filter(m => m.status === 'manifested').length / userManifestations.length) * 100)
                        : 0}%
                    </p>
                  </div>
                </div>
                <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">{t('profile.successRateDescription')}</p>
              </div>
            </div>

            <div className="mt-6 sm:mt-8 bg-gray-50 rounded-lg p-4 sm:p-6 text-center">
              <h3 className="text-sm sm:text-base font-medium text-gray-900 mb-2">{t('profile.yourSuccessJourney')}</h3>
              <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
                {t('profile.successJourneyDescription')}
              </p>
            </div>
          </div>
        )}

        {/* Analytics & Insights Tab */}
        {activeTab === 'analytics-insights' && (
          <AnalyticsInsights />
        )}

        {/* Manifestations Tab */}
        {activeTab === 'manifestations' && (
          <div>
            <div className="flex justify-between items-center mb-4 sm:mb-6">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">{t('profile.myManifestations')}</h2>
            </div>
            
            {manifestationsLoading ? (
              <div className="text-center py-6 sm:py-8">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-purple-500 border-r-transparent"></div>
                <p className="mt-2 text-sm sm:text-base text-gray-600">{t('common.loading')}</p>
              </div>
            ) : userManifestations.length === 0 ? (
              <div className="text-center py-6 sm:py-8 bg-gray-50 rounded-lg">
                <Star size={40} className="sm:w-12 sm:h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-sm sm:text-base text-gray-600 mb-4 px-4">You haven't created any manifestations yet.</p>
                <p className="text-gray-500 text-xs sm:text-sm px-4">
                  Talk to Manivo to create your first manifestation!
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {userManifestations.map((manifestation) => (
                  <div key={manifestation.id} className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 hover:shadow-sm transition-shadow duration-200">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 mb-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            manifestation.status === 'manifested' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {manifestation.status === 'manifested' ? t('profile.manifested') : t('profile.active')}
                          </span>
                          <span className="text-xs sm:text-sm text-gray-500">
                            {new Date(manifestation.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-sm sm:text-base text-gray-700 leading-relaxed">"{manifestation.manifestation_text}"</p>
                      </div>
                      <div className="flex space-x-1 sm:space-x-2 ml-3 sm:ml-4 flex-shrink-0">
                        {manifestation.status === 'active' && (
                          <button 
                            onClick={() => handleManifestationStatusChange(manifestation.id, 'manifested')}
                            className="p-1 sm:p-2 text-gray-400 hover:text-green-600 transition-colors duration-200"
                            title="Mark as manifested"
                          >
                            <CheckCircle size={14} className="sm:w-4 sm:h-4" />
                          </button>
                        )}
                        {manifestation.status === 'manifested' && (
                          <button 
                            onClick={() => handleManifestationStatusChange(manifestation.id, 'active')}
                            className="p-1 sm:p-2 text-gray-400 hover:text-purple-600 transition-colors duration-200"
                            title="Mark as active"
                          >
                            <Star size={14} className="sm:w-4 sm:h-4" />
                          </button>
                        )}
                        <button 
                          onClick={() => handleManifestationStatusChange(manifestation.id, 'deleted')}
                          className="p-1 sm:p-2 text-gray-400 hover:text-red-600 transition-colors duration-200"
                          title="Delete manifestation"
                        >
                          <Trash2 size={14} className="sm:w-4 sm:h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Books Tab */}
        {activeTab === 'books' && (
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">{t('profile.myLibrary')}</h2>
            <div className="text-center py-6 sm:py-8 bg-gray-50 rounded-lg">
              <BookOpen size={40} className="sm:w-12 sm:h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-sm sm:text-base text-gray-600 mb-4 px-4">You haven't purchased any books yet.</p>
              <p className="text-gray-500 text-xs sm:text-sm px-4">
                Visit the library to discover transformative books.
              </p>
            </div>
          </div>
        )}

        {/* Gratitude Tab */}
        {activeTab === 'gratitude' && (
          <div>
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">{t('profile.dailyGratitudePractice')}</h2>
            <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
              <div className="text-center mb-6 sm:mb-8">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Heart size={16} className="sm:w-5 sm:h-5 text-gray-600" />
                </div>
                <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">
                  {t('profile.todaysGratitudeFor')} {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                </h3>
                <p className="text-gray-600 text-xs sm:text-sm px-2">{t('profile.listThingsGrateful')}</p>
              </div>

              {/* Add New Gratitude */}
              <div className="mb-4 sm:mb-6">
                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                  <input
                    type="text"
                    value={newGratitude}
                    onChange={(e) => setNewGratitude(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addGratitude()}
                    placeholder={t('profile.whatGratefulToday')}
                    className="flex-1 px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm"
                  />
                  <button
                    onClick={addGratitude}
                    className="bg-earth-bronze hover:bg-earth-warmstone text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg text-xs sm:text-sm font-medium transition-colors duration-200"
                  >
                    {t('profile.add')}
                  </button>
                </div>
              </div>

              {/* Gratitude List */}
              <div className="space-y-3">
                {gratitudeItems.map((item, index) => (
                  <div key={index} className="flex items-start space-x-3 sm:space-x-4 bg-gray-50 rounded-lg p-3 sm:p-4">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 bg-earth-bronze rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-white text-xs font-medium">{index + 1}</span>
                    </div>
                    <p className="flex-1 text-gray-700 text-xs sm:text-sm leading-relaxed">{item}</p>
                    <button
                      onClick={() => removeGratitude(index)}
                      className="p-1 text-gray-400 hover:text-red-600 flex-shrink-0"
                    >
                      <Trash2 size={12} className="sm:w-3.5 sm:h-3.5" />
                    </button>
                  </div>
                ))}
              </div>

              {gratitudeItems.length >= 3 && (
                <div className="mt-6 sm:mt-8">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4 text-center">
                    <p className="text-green-800 text-xs sm:text-sm font-medium">
                      {t('profile.greatJobCompleted')}
                    </p>
                    <p className="text-green-700 text-xs mt-1">
                      {t('profile.gratitudeRaisesVibration')}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};